import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import NewMemberForm from "components/NewMemberForm";
import {
  initialEditingUser,
  setIsEditingPrimaryUser,
  setPrimaryUser,
  setSecondaryUsers,
} from "features/slices/buyFlowSlice";
import { setStep } from "features/slices/shopPagePhaseTwoSlice";
import { BuyFlowUser } from "features/types";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { trackConversion } from "queries/conversion.queries";
import { useFetchPlanPackagesQuery } from "queries/planPackage.queries";
import React, { forwardRef, useState } from "react";
import { View } from "react-native";
import { emailExistsInSecondaryUsers } from "screens/Register/registerUtils";
import userServices from "services/user.services";
import { getPlanType } from "utils/planPackageUtils";
import toast from "utils/toast";
import { getUserName } from "utils/userUtils";
import { planIdProperty } from "./helpers";

const StepThree = forwardRef(function StepThree(
  { createAccount }: { createAccount: () => void },
  ref: any
) {
  const dispatch = useAppDispatch();

  const [addMemberFormVisible, setAddMemberFormVisible] = useState(false);
  const [addMemberFormInitialValues, setAddMemberFormInitialValues] =
    useState<BuyFlowUser>(initialEditingUser);

  // When user is editing a member, this will be the index of the member being edited
  const [editMemberIndex, setEditMemberIndex] = useState<number | null>(null);

  const { selectedCmsPlan, step } = useAppSelector(
    (state) => state.shopPagePhaseTwo
  );
  const { isEditingPrimaryUser, primaryUser, secondaryUsers } = useAppSelector(
    (state) => state.buyFlow
  );

  const { mutateAsync: track } = useMutation(trackConversion);

  const { data: planPackages } = useFetchPlanPackagesQuery();

  const planType = () => {
    const selectedPlanPackage = planPackages?.find(
      (p) => p.id === selectedCmsPlan![planIdProperty]
    );
    if (!selectedPlanPackage) return null;
    return getPlanType(selectedPlanPackage);
  };

  const handleMemberSubmit = async (values: BuyFlowUser) => {
    if (editMemberIndex !== null) {
      const newMembers = [...secondaryUsers];
      newMembers[editMemberIndex] = values;
      dispatch(setSecondaryUsers(newMembers));
      setEditMemberIndex(null);
    } else {
      if (values.email!.toLowerCase() === primaryUser.email!.toLowerCase()) {
        toast.error("Email is used by the primary member");
        return;
      }
      // checking if the email already exists in the secondary users
      if (emailExistsInSecondaryUsers(values.email!, secondaryUsers)) {
        toast.error("Email is used by another member");
        return;
      }
      // checking if the email already exists in the database
      const userExisted = await userServices.userExists(values.email!);
      if (userExisted) {
        toast.error(`Email ${values.email} is already in registered`);
        return;
      }

      dispatch(setSecondaryUsers([...secondaryUsers, values]));
    }
    setAddMemberFormVisible(false);
  };

  return (
    <div ref={ref}>
      <Text category="h6">Personal Information</Text>
      {!isEditingPrimaryUser && (
        <View className="bg-white rounded-xl p-4 my-4 shadow-lg font-semibold text-lg">
          <View className="flex-row items-center justify-between">
            <View>
              <span>{getUserName(primaryUser)}</span>
              <span>{primaryUser.email?.toLowerCase()}</span>
              <span>{primaryUser.phone}</span>
            </View>
            <Button
              appearance="ghost"
              onPress={() => dispatch(setIsEditingPrimaryUser(true))}
            >
              Edit
            </Button>
          </View>

          {planType() === "RESERVATION" && (
            <Button
              appearance="ghost"
              onPress={() => {
                setAddMemberFormVisible(true);
                setAddMemberFormInitialValues(initialEditingUser);
                setEditMemberIndex(null);
              }}
            >
              Add Member
            </Button>
          )}

          {addMemberFormVisible && (
            <View>
              <div className="mb-4">Additional Member Info:</div>
              <NewMemberForm
                initialValues={addMemberFormInitialValues}
                onSubmit={handleMemberSubmit}
                showImageUploader={false}
                btnText={isEditingPrimaryUser ? "Update User" : "Add User"}
              />
              <View className="mt-4">
                <Button onPress={() => setAddMemberFormVisible(false)}>
                  Cancel
                </Button>
              </View>
            </View>
          )}
        </View>
      )}
      {isEditingPrimaryUser && (
        <View className="mt-4">
          <NewMemberForm
            initialValues={primaryUser}
            onSubmit={async (values) => {
              dispatch(setPrimaryUser(values));
              dispatch(setIsEditingPrimaryUser(false));
              dispatch(setStep(step + 1));
              await track({
                step: "USER_INFO_ENTERED",
                flow: "SHOP_PHASE_TWO",
              });
            }}
            includePassword
            showImageUploader={false}
            btnText={primaryUser.email ? "Update User" : "Add User"}
          />
        </View>
      )}
      {/* start of additional members section */}
      {secondaryUsers.length > 0 && (
        <View className="bg-white rounded-xl p-4 my-4 shadow-lg font-semibold text-lg">
          <div>Additional Members:</div>
          {secondaryUsers.map((member, i) => (
            <View
              key={i}
              className="flex-row items-center justify-between my-6"
            >
              <View>
                <span>{getUserName(member)}</span>
                <span>{member.email?.toLowerCase()}</span>
                <span>{member.phone}</span>
              </View>
              <View>
                <Button
                  appearance="ghost"
                  onPress={() => {
                    setAddMemberFormVisible(true);
                    setAddMemberFormInitialValues(member);
                    setEditMemberIndex(i);
                  }}
                >
                  Edit
                </Button>
                <Button
                  appearance="ghost"
                  onPress={() =>
                    dispatch(
                      setSecondaryUsers(
                        secondaryUsers.filter(
                          (_, secondaryUserIndex) => secondaryUserIndex !== i
                        )
                      )
                    )
                  }
                >
                  Delete
                </Button>
              </View>
            </View>
          ))}
        </View>
      )}
    </div>
  );
});

export default StepThree;
